import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import Nav from './Nav';
import './ForgotPassword.css';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Token de restablecimiento no proporcionado. El enlace puede ser inválido.');
    }
  }, [token]);

  const isStrongPassword = (password) => {
    if (password.length < 8) return false;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*()_+=[\]{};':"\\|,.<>/?-]/.test(password);
    return hasUppercase && hasLowercase && hasNumber && hasSpecialChar;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validation
    if (newPassword.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres');
      setLoading(false);
      return;
    }

    if (!isStrongPassword(newPassword)) {
      setError('La contraseña debe contener mayúsculas, minúsculas, números y caracteres especiales');
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      setLoading(false);
      return;
    }

    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
      const response = await axios.post(`${API_BASE}/auth/reset-password`, {
        token,
        newPassword
      });

      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login', { state: { from: 'internal' } });
        }, 3000);
      } else {
        setError(response.data.msg || 'Error al restablecer la contraseña');
      }
    } catch (err) {
      console.error('Error:', err);
      setError(err.response?.data?.msg || 'Error al restablecer la contraseña');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <>
        <Nav />
        <div className="forgot-page">
          <div className="forgot-card">
            <div className="forgot-success">
              <div className="success-icon">✓</div>
              <h2 className="forgot-title">Contraseña restablecida</h2>
              <p className="forgot-subtitle">Tu contraseña ha sido restablecida exitosamente.</p>
              <p className="forgot-hint">Serás redirigido al inicio de sesión en 3 segundos...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Nav />
      <div className="forgot-page">
        <div className="forgot-card">
          <div className="forgot-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="#FF6600" strokeWidth="1.5" width="36" height="36">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>

          <h2 className="forgot-title">Restablecer contraseña</h2>
          <p className="forgot-subtitle">Ingresa tu nueva contraseña</p>

          {error && (
            <div style={{
              color: '#ff4444',
              marginBottom: '1rem',
              padding: '0.5rem',
              background: 'rgba(255, 68, 68, 0.1)',
              borderRadius: '4px'
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="forgot-form">
            <div className="forgot-field">
              <label className="forgot-label">Nueva contraseña</label>
              <div style={{ position: 'relative' }}>
                <input
                  className="forgot-input"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#666'
                  }}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            <div className="forgot-field">
              <label className="forgot-label">Confirmar contraseña</label>
              <input
                className="forgot-input"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>

            <div style={{
              fontSize: '0.85rem',
              color: '#666',
              marginBottom: '1rem',
              padding: '0.75rem',
              background: '#f5f5f5',
              borderRadius: '4px'
            }}>
              <p style={{ margin: '0 0 0.5rem 0' }}>La contraseña debe tener:</p>
              <ul style={{ margin: '0.5rem 0 0 1.5rem', paddingLeft: 0 }}>
                <li>Mínimo 8 caracteres</li>
                <li>Al menos una mayúscula (A-Z)</li>
                <li>Al menos una minúscula (a-z)</li>
                <li>Al menos un número (0-9)</li>
                <li>Al menos un carácter especial (!@#$%^&*)</li>
              </ul>
            </div>

            <button type="submit" className="forgot-btn-submit" disabled={loading}>
              {loading ? '...' : 'Restablecer contraseña'}
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
